import { useCallback, useEffect, useRef, useState, type CSSProperties, type TransitionEvent as ReactTransitionEvent, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { BarChart3, Users, Cpu, Megaphone, Brain, ArrowLeft, Shield, ListTodo, ScrollText, Menu, X, GitBranch, FileText, DollarSign, Settings, Mail, HelpCircle, Code2, Activity, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import {
  NAV_COLLAPSED_WIDTH,
  NAV_MAX_WIDTH,
  clampNavWidth,
  effectiveNavWidth,
  loadNavState,
  resolveDragWidth,
  saveNavState,
  type NavState,
} from '../../utils/adminNav';

/** Keyboard resize step for the drag handle (arrow keys). */
const NAV_KEY_STEP = 16;

/** Tell width-sensitive pages (cytoscape canvas, charts) to re-measure. */
function notifyResize() {
  window.dispatchEvent(new Event('resize'));
}

export function AdminLayout() {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Desktop (md+) collapse/resize state, persisted in localStorage.
  const [nav, setNav] = useState<NavState>(() => loadNavState());
  const [dragging, setDragging] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ startX: number; startW: number; lastRaw: number; moved: boolean; raf: number } | null>(null);

  useEffect(() => { saveNavState(nav); }, [nav]);

  const collapsed = nav.collapsed;
  const navWidth = effectiveNavWidth(nav);

  const toggleCollapsed = useCallback(() => {
    setNav((s) => ({ ...s, collapsed: !s.collapsed }));
  }, []);

  // Width transition finished (collapse/expand) → let canvases re-measure.
  const onAsideTransitionEnd = useCallback((e: ReactTransitionEvent<HTMLElement>) => {
    if (e.target === e.currentTarget && e.propertyName === 'width') notifyResize();
  }, []);

  const onHandlePointerDown = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { startX: e.clientX, startW: navWidth, lastRaw: navWidth, moved: false, raf: 0 };
    setDragging(true);
  }, [navWidth]);

  const onHandlePointerMove = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    if (!d) return;
    const raw = d.startW + (e.clientX - d.startX);
    if (!d.moved && Math.abs(e.clientX - d.startX) < 3) return;
    d.moved = true;
    d.lastRaw = raw;
    if (d.raf) return;
    // Live preview via the CSS var only (no React render per pointermove).
    d.raf = requestAnimationFrame(() => {
      d.raf = 0;
      const preview = resolveDragWidth(d.lastRaw, nav.width);
      const w = preview.collapsed ? NAV_COLLAPSED_WIDTH : clampNavWidth(d.lastRaw);
      rootRef.current?.style.setProperty('--admin-nav-w', `${w}px`);
    });
  }, [nav.width]);

  const endDrag = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    if (!d) return;
    dragRef.current = null;
    if (d.raf) cancelAnimationFrame(d.raf);
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
    setDragging(false);
    if (d.moved) {
      const next = resolveDragWidth(d.lastRaw, nav.width);
      // Pin the CSS var explicitly: if the committed state equals the previous
      // one React won't rewrite the style, leaving the live-preview value.
      rootRef.current?.style.setProperty('--admin-nav-w', `${effectiveNavWidth(next)}px`);
      setNav(next);
      requestAnimationFrame(notifyResize);
    }
  }, [nav.width]);

  const onHandleKeyDown = useCallback((e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleCollapsed();
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
      const delta = e.key === 'ArrowLeft' ? -NAV_KEY_STEP : NAV_KEY_STEP;
      setNav((s) => resolveDragWidth(effectiveNavWidth(s) + delta, s.width));
    }
  }, [toggleCollapsed]);

  // Collapsed-rail helpers: labels fade out (desktop only) and are clipped.
  const labelCls = `whitespace-nowrap overflow-hidden transition-opacity duration-150 ${collapsed ? 'md:opacity-0' : 'opacity-100'}`;
  const rootStyle = { '--admin-nav-w': `${navWidth}px` } as CSSProperties;
  const widthTransition = dragging ? '' : 'md:transition-[transform,width] md:duration-200';

  const navItems = [
    { to: '/admin/f47ac10b58cc/dashboard', label: 'Dashboard', icon: BarChart3 },
    { to: '/admin/f47ac10b58cc/canary-analytics', label: 'Canary Analytics', icon: Activity },
    { to: '/admin/f47ac10b58cc/users', label: 'Users', icon: Users },
    { to: '/admin/f47ac10b58cc/pricing', label: 'Pricing', icon: DollarSign },
    { to: '/admin/f47ac10b58cc/data-flows', label: 'Data Flows', icon: GitBranch },
    { to: '/admin/f47ac10b58cc/marketing', label: 'Marketing', icon: Megaphone },
    { to: '/admin/f47ac10b58cc/models', label: 'AI Config', icon: Brain },
    { to: '/admin/f47ac10b58cc/ai-usage', label: 'AI Usage', icon: Cpu },
    { to: '/admin/f47ac10b58cc/pdf-calibration', label: 'PDF Calibration', icon: FileText },
    { to: '/admin/f47ac10b58cc/work-queue', label: 'Work Queue', icon: ListTodo },
    { to: '/admin/f47ac10b58cc/website-dev', label: 'Website Dev', icon: Code2 },
    { to: '/admin/f47ac10b58cc/communications', label: 'Communications', icon: Mail },
    { to: '/admin/f47ac10b58cc/help-sections', label: 'Help Docs', icon: HelpCircle },
    { to: '/admin/f47ac10b58cc/global-settings', label: 'Global Settings', icon: Settings },
    { to: '/admin/f47ac10b58cc/logs', label: 'Logs', icon: ScrollText },
  ];

  return (
    <div
      ref={rootRef}
      style={rootStyle}
      className={`min-h-screen flex bg-gray-100 ${dragging ? 'select-none cursor-col-resize' : ''}`}
      data-testid="admin-layout"
      data-nav-collapsed={collapsed ? 'true' : 'false'}
    >
      {/* Mobile header bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-gray-900 text-white flex items-center gap-2 px-4 py-3">
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-1">
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
        <Shield className="w-5 h-5 text-primary-400" />
        <span className="font-bold">SkillSnap Admin</span>
      </div>

      {/* Mobile overlay backdrop */}
      {sidebarOpen && (
        <div
          className="md:hidden fixed inset-0 z-30 bg-black/50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar (mobile: w-64 slide-in drawer; md+: collapsible/resizable) */}
      <aside
        data-testid="admin-sidebar"
        onTransitionEnd={onAsideTransitionEnd}
        className={`
        fixed z-30 top-0 left-0 h-full w-64 md:w-[var(--admin-nav-w)] bg-gray-900 text-white flex flex-col overflow-y-auto overflow-x-hidden
        transform transition-transform duration-200 ease-in-out ${widthTransition}
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0
      `}
      >
        <div className={`p-4 border-b border-gray-700 ${collapsed ? 'md:px-2' : ''}`}>
          <div className={`flex items-center gap-2 ${collapsed ? 'md:flex-col md:gap-3' : ''}`}>
            <Shield className="w-6 h-6 flex-shrink-0 text-primary-400" />
            <span className={`text-lg font-bold ${labelCls} ${collapsed ? 'md:hidden' : ''}`}>SkillSnap Admin</span>
            <button
              type="button"
              onClick={toggleCollapsed}
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-expanded={!collapsed}
              data-testid="admin-nav-toggle"
              className={`hidden md:inline-flex flex-shrink-0 items-center justify-center p-1 rounded-md text-gray-400 hover:text-white hover:bg-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 ${collapsed ? '' : 'ml-auto'}`}
            >
              {collapsed ? <PanelLeftOpen className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <nav className={`flex-1 py-4 space-y-1 transition-[padding] duration-200 ${collapsed ? 'px-4 md:px-2' : 'px-4'}`}>
          {navItems.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              onClick={() => setSidebarOpen(false)}
              title={collapsed ? label : undefined}
              aria-label={label}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg overflow-hidden transition-colors ${
                  isActive
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                }`
              }
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              <span className={labelCls}>{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className={`p-4 border-t border-gray-700 transition-[padding] duration-200 ${collapsed ? 'md:px-[22px]' : ''}`}>
          <button
            onClick={() => navigate('/')}
            title={collapsed ? 'Back to App' : undefined}
            aria-label="Back to App"
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors w-full overflow-hidden"
          >
            <ArrowLeft className="w-4 h-4 flex-shrink-0" />
            <span className={labelCls}>Back to App</span>
          </button>
        </div>
      </aside>

      {/* Drag handle on the sidebar's right edge (md+ only). Drag to resize,
          drag far left to collapse, double-click / Enter to toggle. */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize sidebar"
        aria-valuemin={NAV_COLLAPSED_WIDTH}
        aria-valuemax={NAV_MAX_WIDTH}
        aria-valuenow={navWidth}
        title="Drag to resize · double-click to collapse/expand"
        tabIndex={0}
        data-testid="admin-nav-resize"
        onPointerDown={onHandlePointerDown}
        onPointerMove={onHandlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onDoubleClick={toggleCollapsed}
        onKeyDown={onHandleKeyDown}
        style={{ left: 'calc(var(--admin-nav-w) - 3px)' }}
        className={`hidden md:block fixed top-0 bottom-0 z-40 w-1.5 cursor-col-resize touch-none group focus:outline-none ${dragging ? '' : 'md:transition-[left] md:duration-200'}`}
      >
        <div className={`mx-auto h-full w-0.5 transition-colors ${dragging ? 'bg-primary-400' : 'bg-transparent group-hover:bg-primary-400/70 group-focus-visible:bg-primary-400'}`} />
      </div>

      {/* Main Content */}
      <main className={`flex-1 overflow-auto pt-12 md:pt-0 md:ml-[var(--admin-nav-w)] ${dragging ? '' : 'md:transition-[margin-left] md:duration-200'}`}>
        <Outlet />
      </main>
    </div>
  );
}
